// src/pages/equipmentrentaladmin/conversation/AdminConversations.jsx

import React, { useState, useRef, useEffect, useContext } from "react";
import { FiSearch, FiSend, FiPaperclip, FiTruck } from "react-icons/fi";
import "../../../assets/css/equipmentrentaladmin/conversation/Conversation.css";
import RentalAdminLayout from "../../../layout/equipmentrentaladmin/RentalAdminLayout";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import { useLocation } from "react-router-dom";
import { getEcho } from "../../../utils/echo";

// ─── Avatar color pool ─────────────────────────────────────────────────────────
const avatarColors = ["#FDB714", "#000000", "#2ecc71", "#e67e22"];

export default function AdminConversations() {
  const { baseUrl, baseUrlForFile, loggedInRental , authToken} = useContext(AppContext);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMsg, setNewMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); 
  const [attachedFile, setAttachedFile] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const location = useLocation();

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── PUSHER — real-time naye messages sunta hai jo select ki hui
  //    conversation mein aayen (jab tak yeh chat khuli hai) ──
  useEffect(() => {
    if (!selectedChat) return;

    const echo = getEcho(baseUrl);
    if (!echo) return;

    const channel = echo.private(`conversation.${selectedChat.id}`);

    channel.listen(".new-message", (e) => {
      setMessages((prev) => [...prev, e.chat]);
    });

    return () => {
      echo.leave(`conversation.${selectedChat.id}`);
    };
  }, [selectedChat, baseUrl]);

  // ── fetchAllConversations ──────────────────────────────────────────────────
  const fetchAllConversations = () => {
    const savedRental = JSON.parse(localStorage.getItem('LoggedInRental'));
    const formData = new FormData();
    formData.append('srrentaladmin_id', savedRental.id);

    axios
      .post(`${baseUrl}/rentaladmin/conversations/fetchAllConversations`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' ,
          Authorization: `Bearer ${authToken}`
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setConversations(res.data.conversations);
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
        }
      })
      .catch((err) => {
        console.log('Error fetching conversations:', err);
      });
  };

  useEffect(() => {
    fetchAllConversations();
  }, []);

  // ── fetchAllMessages ───────────────────────────────────────────────────────
  const fetchAllMessages = (conversationId) => {
    const formData = new FormData();
    formData.append('srconversation_id', conversationId);

    axios
      .post(`${baseUrl}/rentaladmin/chat/fetchAllMessages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' ,
          Authorization: `Bearer ${authToken}`
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setMessages(res.data.messages);
        } else {
          setMessages([]);
        }
      })
      .catch((err) => {
        console.log('Error fetching messages:', err);
      });
  };

  // ── sendMessage ────────────────────────────────────────────────────────────
  const sendMessage = () => {
    if (!newMsg.trim() && !attachedFile) return;
    if (!selectedChat) return;

    const formData = new FormData();
    formData.append('srconversation_id', selectedChat.id);
    formData.append('sender', 'rental');
    formData.append('message', newMsg);
    if (attachedFile) {
      formData.append('image', attachedFile);
    }

    axios
      .post(`${baseUrl}/rentaladmin/chat/sendMessage`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' ,
          Authorization: `Bearer ${authToken}`
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setNewMsg('');
          setAttachedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          fetchAllMessages(selectedChat.id);
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
        }
      })
      .catch((err) => {
        console.log('Error sending message:', err);
      });
  };


  // ── handleSelectConv ───────────────────────────────────────────────────────
  const handleSelectConv = (conv) => {
    setSelectedChat(conv);
    fetchAllMessages(conv.id);
  };

  // ── Filter conversations ───────────────────────────────────────────────────
  const filtered = conversations?.filter((c) => {
    const customerName = c.srcustomer?.fullname?.toLowerCase() || '';
    const term = searchTerm.toLowerCase();
    return customerName.includes(term);
  });

  // ── formatTime ────────────────────────────────────────────────────────────
  const formatTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };


  useEffect(() => {

  if (
    conversations.length > 0 &&
    location.state?.openConversationId
  ) {

    const targetConversation =
      conversations.find(
        (c) =>
          c.id ===
          location.state.openConversationId
      );

    if (targetConversation) {

      setSelectedChat(
        targetConversation
      );

      fetchAllMessages(
        targetConversation.id
      );
    }
  }

}, [
  conversations,
  location.state
]);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <RentalAdminLayout>
      <div className="ac-root">

        {/* ── SIDEBAR ── */}
        <aside className="ac-sidebar">
          <div className="ac-sidebar-header">
            <div className="ac-header-icon">💬</div>
            <div>
              <h2 className="ac-sidebar-title">Messages</h2>
              <p className="ac-sidebar-sub">{conversations?.length} conversations</p>
            </div>
          </div>

          <div className="ac-search-wrap">
            <FiSearch className="ac-search-icon" />
            <input
              className="ac-search-input"
              placeholder="Search farmer or equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="ac-conv-list">
            {filtered?.map((conv, idx) => {

              const displayName =
                conv.srmechanicadmin?.fullname ||
                conv.srcustomer?.fullname ||
                "Unknown";

              const userType =
                conv.srmechanicadmin
                  ? "Mechanic"
                  : "Customer";

              return (
                <div
                  key={conv.id}
                  className={`ac-conv-item ${selectedChat?.id === conv.id ? "ac-conv-active" : ""
                    }`}
                  onClick={() => handleSelectConv(conv)}
                >
                  <div
                    className="ac-avatar"
                    style={{
                      background: avatarColors[idx % avatarColors.length],
                      color: idx === 0 ? "#000" : "#fff",
                    }}
                  >
                    {displayName?.charAt(0)?.toUpperCase()}
                  </div>

                  <div className="ac-conv-info">
                    <div className="ac-conv-top">
                      <span className="ac-conv-name">{displayName}</span>

                      <small
                        style={{
                          color: "#777",
                          marginLeft: "8px",
                          fontSize: "12px",
                        }}
                      >
                        {userType}
                      </small>

                      <span className="ac-conv-time">
                        {formatTime(conv.updated_at)}
                      </span>
                    </div>

                    <div className="ac-conv-eq">
                      <FiTruck
                        size={11}
                        style={{ marginRight: 4, flexShrink: 0 }}
                      />
                      {conv.equipment}
                    </div>

                    <div className="ac-conv-bottom">
                      <span className="ac-conv-preview">
                        {conv.lastmessage}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            {filtered?.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                <p>No conversations found</p>
              </div>
            )}
          </div>
        </aside>

        {/* ── MAIN CHAT ── */}
        {selectedChat ? (
          <main className="ac-main">

            {/* Header */}
            <div className="ac-chat-header">
              <div
                className="ac-avatar"
                style={{ background: '#FDB714', color: '#000' }}
              >
                {(
                  selectedChat.srmechanicadmin?.fullname ||
                  selectedChat.srcustomer?.fullname ||
                  "U"
                )
                  .charAt(0)
                  .toUpperCase()}
              </div>
              <div className="ac-chat-header-info">
                <h3 className="ac-chat-farmer-name">
                  {selectedChat.srmechanicadmin?.fullname ||
                    selectedChat.srcustomer?.fullname}
                </h3>

                <span style={{ color: "#666", fontSize: "13px" }}>
                  {selectedChat.srmechanicadmin
                    ? "Mechanic"
                    : "Customer"}
                </span>
              </div>
            </div>

            {/* Equipment info bar */}
            <div className="ac-eq-bar">
              <FiTruck size={14} />
              <span>Equipment:</span>
              <strong>{selectedChat.equipmentname}</strong>
              <span className="ac-eq-divider">|</span>
              <span>Farmer:</span>
              <strong>{selectedChat.srcustomer?.fullname}</strong>
            </div>

            {/* Messages */}
            <div className="ac-messages">
              {messages?.map((msg) => {
                const isAdmin = msg.sender === 'rental';
                // const senderClass = isAdmin ? 'sent' : 'received';

                return (
                  <div
                    key={msg.id}
                    className={`ac-msg-row ${isAdmin ? 'ac-msg-sent' : 'ac-msg-received'}`}
                  >
                    <div className="ac-bubble-wrap">
                      <div className={`ac-bubble ${isAdmin ? 'ac-bubble-sent' : 'ac-bubble-received'}`}>
                        {msg.image && msg.image.trim() !== '' && (
                          <img
                            src={`${baseUrlForFile}/${msg.image}`}
                            alt="attachment"
                            style={{
                              maxWidth: '200px',
                              borderRadius: '8px',
                              marginBottom: '4px',
                              display: 'block',
                            }}
                            onError={(e) => {
                              console.log('Image failed to load:', e.target.src);
                            }}
                          />
                        )}
                        {msg.message && msg.message.trim() !== '' && (
                          <span>{msg.message}</span>
                        )}
                      </div>
                      <span className="ac-msg-time">{formatTime(msg.created_at)}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* File Preview */}
            {attachedFile && (
              <div style={{ padding: '6px 16px', background: '#f0f0f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📎 {attachedFile.name}</span>
                <button
                  onClick={() => {
                    setAttachedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Input */}
            <div className="ac-input-wrap">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={(e) => setAttachedFile(e.target.files[0])}
              />

              <button
                className="ac-attach-btn"
                onClick={() => fileInputRef.current.click()}
              >
                <FiPaperclip size={17} />
              </button>

              <input
                className="ac-text-input"
                placeholder="Type your reply..."
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />

              <button
                className="ac-send-btn"
                onClick={sendMessage}
                disabled={!newMsg.trim() && !attachedFile}
              >
                <FiSend size={16} />
              </button>
            </div>

            <p className="ac-footer-text">PROFESSIONAL AGRICULTURAL EQUIPMENT RENTAL</p>
          </main>
        ) : (
          <main className="ac-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#888' }}>
            <div style={{ fontSize: '48px' }}>💬</div>
            <h3>Select a conversation</h3>
            <p>Choose a farmer to start messaging</p>
          </main>
        )}

      </div>
    </RentalAdminLayout>
  );
}